import { ActionIcon, Box, Button, Text } from "@mantine/core";
import { Link, useLocation } from "react-router-dom";
import {
  IconBook,
  IconBrowserCheck,
  IconBuilding,
  IconCash,
  IconFile,
  IconGift,
  IconHelp,
  IconHome,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";
import "./Sidebar.scss";
import { AppRoutes, support_url } from "../../constants";
import { useAuthStore } from "@utils/stores/authStore.ts";
import { initializeIntercom } from "@utils/intercom.ts";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { openNewTab } from "@/utils/browserUtils";
import { getIntakeServiceBureauHomeUrl } from "@/utils/intakeUrl";
import { LegacyDeprecationBanner } from "@/components/FrontendMigration";

interface SidebarProps {
  condensed?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ condensed }) => {
  const location = useLocation();
  const { role, isOwner, isTeamLead, isServiceBureauUser } = useAuthStore();
  const { hasFlag } = useFeatureFlags();
  const personalInfoString = localStorage.getItem("personalInfo");
  const personalInfo = personalInfoString ? JSON.parse(personalInfoString) : {};

  const data = [
    {
      route: role === "client" ? AppRoutes.clientHome : AppRoutes.home,
      label: "Home",
      icon: IconHome,
    },
    {
      route:
        role === "client"
          ? `/clients/${personalInfo?.userId}`
          : AppRoutes.clients,
      label: role === "client" ? "Documents" : "Clients",
      icon: role === "client" ? IconFile : IconUser,
    },
    ...(role === "client"
      ? [
          {
            route: AppRoutes.taxOrganizerHome,
            label: "Tax Organizer",
            icon: IconBrowserCheck,
          },
        ]
      : []),
    ...(role !== "client"
      ? [
          ...(hasFlag("LEGACY_PAYMENTS")
            ? [
                {
                  route: AppRoutes.payments,
                  label: "Payments",
                  icon: IconCash,
                },
              ]
            : []),
          ...(isOwner() || isTeamLead()
            ? [
                {
                  route: AppRoutes.teamSettings,
                  label: "Team",
                  icon: IconUsers,
                },
              ]
            : []),
          ...(isServiceBureauUser()
            ? [
                {
                  externalUrl: getIntakeServiceBureauHomeUrl(),
                  label: "Service bureau",
                  icon: IconBuilding,
                },
              ]
            : []),
          {
            route: AppRoutes.referralSettings,
            label: "Referrals",
            icon: IconGift,
          },
        ]
      : []),
  ];

  const items = data.map((item, index) => {
    const currentPath = `${location.pathname}${location.search}`;
    const isActive =
      !("externalUrl" in item) &&
      item &&
      (currentPath === item.route || location.pathname === item.route);

    const itemContent = (
      <div className={`left ${isActive ? "active" : ""}`}>
        {condensed ? (
          <ActionIcon variant="transparent">
            {item?.icon && <item.icon />}
          </ActionIcon>
        ) : (
          <>
            {item?.icon && <item.icon />}
            {item && <Text className="label">{item.label}</Text>}
          </>
        )}
      </div>
    );

    if ("externalUrl" in item && item.externalUrl) {
      return (
        <a
          key={index}
          href={item.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`sidebar-item ${condensed ? "condensed" : ""}`}
        >
          {itemContent}
        </a>
      );
    }

    return (
      <Link
        key={index}
        to={item?.route ?? "#"}
        className={`sidebar-item ${condensed ? "condensed" : ""} ${
          isActive ? "active" : ""
        }`}
      >
        {itemContent}
      </Link>
    );
  });

  return (
    <Box className="sidebar-container" w={220}>
      <div className="sidebar-body">{items}</div>

      <div className="sidebar-footer">
        {!condensed && <LegacyDeprecationBanner />}
        {role !== "client" && (
          <Button
            className="outlinedBtn"
            leftSection={!condensed && <IconHelp size={22} />}
            onClick={() => {
              openNewTab(support_url);
              initializeIntercom();
            }}
          >
            {condensed ? <IconBook size={18} /> : "Smart Hub"}
          </Button>
        )}
      </div>
    </Box>
  );
};

export default Sidebar;
